<template>
  <header class="sidd-chat-header">
    <div class="sidd-chat-header__content">
      <div class="sidd-chat-header__center">
        <div v-if="isLoading" class="sidd-chat-header__loading">
          <div class="sidd-chat-header__skeleton"></div>
        </div>

        <div v-else class="sidd-chat-header__title-section">
          <h1 class="sidd-chat-header__title">{{ displayTitle }}</h1>
        </div>
      </div>

      <div class="sidd-chat-header__right">
        <ClearHistoryButton />
        <Button
          v-if="showSourcesButton"
          variant="ghost"
          size="icon"
          class="sidd-chat-header__source"
          @click="handleSource"
          :aria-label="sourceButtonLabel"
        >
          <FileText :size="20" />
        </Button>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Button } from '@/lib/ui/button'
import { FileText } from 'lucide-vue-next'
import { useChatbot } from '@/composables/useChatbot'
import ClearHistoryButton from './ClearHistoryButton.vue'
import type { IHeaderSiddStudioProps, IHeaderSiddStudioEmits } from './types'

defineProps<IHeaderSiddStudioProps>()
const emit = defineEmits<IHeaderSiddStudioEmits>()

const chatbot = useChatbot()

const displayTitle = computed(() => {
  return chatbot.store.brand?.name || 'Chat'
})

const showSourcesButton = computed(() => {
  // Store's configuration getter handles SiddDuo override internally
  return chatbot.store.configuration?.showSourceAttribution ?? false
})

const isLoading = computed(() => {
  return !chatbot.store.isInitialized
})

const sourceButtonLabel = computed(() => 'Show sources')

const handleSource = () => {
  emit('source')
}
</script>

<style>
.sidd-chat-header__content {
  justify-content: space-between;
  gap: 12px;
  max-width: 100%;
}

.sidd-chat-header__center {
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: center;
}

.sidd-chat-header__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sidd-chat-header__title-section {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  max-width: 100%;
}

.sidd-chat-header__title {
  font-size: 1.125em;
  font-weight: 600;
  color: hsl(var(--foreground));
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.sidd-chat-header__loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidd-chat-header__skeleton {
  width: 120px;
  height: 20px;
  background: linear-gradient(
    90deg,
    hsl(var(--muted)) 25%,
    hsl(var(--muted) / 0.5) 50%,
    hsl(var(--muted)) 75%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  animation: skeleton 1.5s infinite;
}

@keyframes skeleton {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>
